import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDownIcon, MapPinIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";

const jobPipelineLocationFilterSchema = z.object({
  locations: z.array(z.string()),
});

export const JobPipelineLocationFilter = ({
  allLocations,
  disabled,
  isInDefaultState,
  locations,
  setLocations,
}: {
  allLocations: string[];
  disabled?: boolean;
  isInDefaultState: boolean;
  locations: string[];
  setLocations: (locations: string[]) => void;
}) => {
  const [open, setOpen] = useState(false);

  const form = useForm<z.infer<typeof jobPipelineLocationFilterSchema>>({
    defaultValues: { locations },
    resolver: zodResolver(jobPipelineLocationFilterSchema),
  });

  const handleOpenChange = (open: boolean) => {
    setOpen(open);

    // If we are closing the dropdown here it means we haven't applied
    // the filter, so reset the form to the current locations.
    //
    // We also delay the reset slightly to avoid the reset happening
    // while the dropdown is closing.
    if (!open) {
      setTimeout(() => {
        form.reset({ locations });
      }, 150);
    }
  };

  const handleSubmit = (
    data: z.infer<typeof jobPipelineLocationFilterSchema>,
  ) => {
    setLocations(data.locations);
    setOpen(false);
  };

  // Need to reset the form when locations change as a result of global reset.
  useEffect(() => {
    form.reset({ locations });
  }, [form, locations]);

  return (
    <DropdownMenu onOpenChange={handleOpenChange} open={open}>
      <DropdownMenuTrigger disabled={disabled} asChild>
        <Button
          className="rounded-full"
          variant={isInDefaultState ? "outlineSecondary" : "filterOrSortActive"}
        >
          <MapPinIcon className="h-4 w-4" />
          Location
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[250px] p-0">
        <Form {...form}>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="locations"
              render={() => (
                <FormItem className="p-4">
                  <FormLabel>Location</FormLabel>
                  {allLocations.map((item) => (
                    <FormField
                      key={item}
                      control={form.control}
                      name="locations"
                      render={({ field }) => {
                        return (
                          <FormItem className="flex gap-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value.includes(item)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    field.onChange([...field.value, item]);
                                  } else {
                                    field.onChange(
                                      field.value.filter(
                                        (value) => value !== item,
                                      ),
                                    );
                                  }
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {item}
                            </FormLabel>
                          </FormItem>
                        );
                      }}
                    />
                  ))}
                </FormItem>
              )}
            />
            <div className="flex justify-end border-t border-slate-200 p-2">
              <Button variant="ghost" type="submit">
                Apply
              </Button>
            </div>
          </form>
        </Form>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
